import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, PartyPopper, ArrowRight } from "lucide-react";
import { track } from "@/track";
import { useLang } from "@/i18n";

const links = [
  { key: "nav_why", href: "#why-us" },
  { key: "nav_plans", href: "#plans" },
  { key: "nav_process", href: "#process" },
  { key: "nav_about", href: "#about" },
  { key: "nav_contact", href: "#contact" },
];

const languages = [
  { code: "en", label: "EN" },
  { code: "fr", label: "FR" },
  { code: "hi", label: "हिं" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [bannerHidden, setBannerHidden] = useState(() => sessionStorage.getItem("ea_banner_hidden") === "1");
  const { lang, change, t } = useLang();

  const dismissBanner = () => {
    setBannerHidden(true);
    sessionStorage.setItem("ea_banner_hidden", "1");
  };

  const renderLangToggle = (suffix) => (
    <div className="flex items-center border border-brand/30" data-testid={`language-toggle${suffix}`}>
      {languages.map((l) => (
        <button
          key={l.code}
          onClick={() => change(l.code)}
          data-testid={`lang-${l.code}${suffix}`}
          className={`px-3 py-2 ${l.code === "hi" ? "text-xs tracking-normal" : "text-[10px] tracking-widest"} font-bold transition-colors duration-300 ${
            lang === l.code ? "bg-brand text-white" : "text-cream/50 hover:text-brand-300"
          }`}
        >
          {l.label}
        </button>
      ))}
    </div>
  );

  return (
    <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-navy-900/80 border-b border-brand/10">
      {!bannerHidden && (
        <div className="relative bg-brand/15 border-b border-brand/20" data-testid="launch-banner">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-2 flex items-center justify-center gap-3">
            <PartyPopper size={13} className="text-brand-300 flex-none" />
            <p className="text-[10px] sm:text-[11px] uppercase tracking-[0.15em] text-cream/75 truncate" data-testid="launch-banner-text">
              {t("banner_text")}
            </p>
            <a
              href="#contact"
              onClick={() => {
                track("banner_claim");
                sessionStorage.setItem("ea_prefill", "I want to claim the Launch Special — Basic Plan at $99 CAD. Please share the next steps.");
              }}
              data-testid="launch-banner-cta"
              className="group flex-none inline-flex items-center gap-1.5 text-[10px] sm:text-[11px] uppercase tracking-[0.15em] font-bold text-brand-300 hover:text-brand transition-colors duration-300"
            >
              {t("banner_cta")}
              <ArrowRight size={12} className="transition-transform duration-300 group-hover:translate-x-0.5" />
            </a>
          </div>
          <button
            onClick={dismissBanner}
            aria-label="Hide launch banner"
            data-testid="launch-banner-dismiss-btn"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-cream/40 hover:text-brand-300 transition-colors duration-300"
          >
            <X size={14} />
          </button>
        </div>
      )}
      <nav className="max-w-7xl mx-auto flex items-center justify-between px-6 lg:px-10 py-5">
        <a href="#top" data-testid="nav-logo" className="flex items-center gap-3 group">
          <span className="relative flex items-center justify-center w-12 h-12 rounded-full bg-white p-1.5 ring-1 ring-brand/50 shadow-[0_0_25px_rgba(99,102,241,0.25)] transition-transform duration-300 group-hover:scale-105 overflow-visible">
            <span className="w-full h-full rounded-full overflow-hidden flex items-center justify-center">
              <img src="/logo.webp" alt="Early Appointments Solutions Inc. logo" className="w-full h-full object-contain" data-testid="nav-logo-img" />
            </span>
            <span className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-brand flex items-center justify-center opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300" aria-hidden="true">
              <ArrowUpRight size={11} className="text-white" />
            </span>
          </span>
          <span className="hidden sm:block leading-tight">
            <span className="block font-serif text-lg tracking-wide text-cream">Early Appointments</span>
            <span className="block text-[10px] uppercase tracking-[0.3em] text-brand/80">Solutions Inc.</span>
          </span>
        </a>

        <div className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.key}`}
              className="text-xs uppercase tracking-[0.2em] text-cream/70 hover:text-brand transition-colors duration-300"
            >
              {t(l.key)}
            </a>
          ))}
          {renderLangToggle("")}
          <a
            href="#contact"
            data-testid="nav-get-started-btn"
            onClick={() => track("nav_get_started")}
            className="bg-brand text-white text-xs uppercase tracking-[0.2em] font-bold px-6 py-3 hover:bg-brand-600 transition-colors duration-300"
          >
            {t("nav_cta")}
          </a>
        </div>

        <div className="flex lg:hidden items-center gap-4">
          {renderLangToggle("-mobile")}
          <button
            data-testid="nav-mobile-menu-btn"
            className="text-brand"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="lg:hidden overflow-hidden bg-navy-800 border-t border-brand/10"
          >
            <div className="flex flex-col px-6 py-6 gap-5">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  data-testid={`nav-mobile-link-${l.key}`}
                  onClick={() => setOpen(false)}
                  className="text-sm uppercase tracking-[0.2em] text-cream/80 hover:text-brand transition-colors"
                >
                  {t(l.key)}
                </a>
              ))}
              <a
                href="#contact"
                data-testid="nav-mobile-get-started-btn"
                onClick={() => { setOpen(false); track("nav_get_started"); }}
                className="bg-brand text-white text-xs uppercase tracking-[0.2em] font-bold px-6 py-3 text-center"
              >
                {t("nav_cta")}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
